import { css } from '@emotion/react'
import styled from '@emotion/styled'
import type OpenAI from 'openai'

import { MarkdownRenderer } from './MarkdownRenderer'
import { useRafText } from './useRafText'
import { theme } from '@/Atomic/assets/theme'

// --- Shared base for both message types ---

const messageBase = css`
  max-width: 90%;
  padding: ${theme.space.s} ${theme.space.m};
  border-radius: ${theme.radii.lg};
  font-size: 0.875rem;
  line-height: 1.5;

  p {
    margin: 0 0 0.5em;
    &:last-child {
      margin-bottom: 0;
    }
  }

  ul,
  ol {
    margin: 0.25em 0;
    padding-left: 1.25em;
  }
`

const UserMessage = styled.div`
  ${messageBase}
  align-self: flex-end;
  background: ${theme.colors.background.subtle};
  color: ${theme.colors.text.primary};
  border-bottom-right-radius: ${theme.space['2xs']};
`

const AssistantMessage = styled.div`
  ${messageBase}
  align-self: flex-start;
  max-width: 100%;
  background: transparent;
  color: ${theme.colors.text.primary};
  overflow-wrap: anywhere;
  word-break: break-word;
`

const MessageText = styled.span`
  white-space: pre-wrap;
  word-break: break-word;
`

// --- Component ---

interface MessageBubbleProps {
  message: OpenAI.ChatCompletionMessageParam
  isStreaming?: boolean
  displayOverride?: string
}

export function MessageBubble({
  message,
  isStreaming,
  displayOverride,
}: MessageBubbleProps) {
  if (message.role === 'tool' || message.role === 'system') return null

  const content = displayOverride ?? extractContent(message)
  if (!content) return null

  if (message.role === 'assistant') {
    return (
      <AssistantMessageBubble content={content} isStreaming={isStreaming} />
    )
  }

  return (
    <UserMessage>
      <MessageText>{content}</MessageText>
    </UserMessage>
  )
}

interface AssistantMessageBubbleProps {
  content: string
  isStreaming?: boolean
}

function AssistantMessageBubble({
  content,
  isStreaming,
}: AssistantMessageBubbleProps) {
  const { displayText } = useRafText(content, !!isStreaming)

  return (
    <AssistantMessage>
      <MarkdownRenderer content={displayText} isStreaming={isStreaming} />
    </AssistantMessage>
  )
}

function extractContent(
  message: OpenAI.ChatCompletionMessageParam,
): string | null {
  const content = message.content
  if (typeof content === 'string') return content || null
  if (Array.isArray(content)) {
    const text = content
      .filter(
        (part): part is { type: 'text'; text: string } =>
          'type' in part && part.type === 'text',
      )
      .map((part) => part.text)
      .join('')
    return text || null
  }
  return null
}
